import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { plainToClass } from 'class-transformer';
import { BehaviorSubject, Observable, ReplaySubject, Subject, throwError } from 'rxjs';
import { catchError, map, retry, shareReplay, takeUntil, tap } from 'rxjs/operators';

import { PlayerModel } from '../models/player.model';

const API_ENDPOINT = '/api/player/';

@Injectable({
  providedIn: 'root',
})
export class PlayerService {

  public forceRefresh: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public changePlayerTheme: BehaviorSubject<string>;
  public changePlayer: BehaviorSubject<PlayerModel>;

  public startingGame: BehaviorSubject<PlayerModel> = new BehaviorSubject<PlayerModel>(null);

  private activePlayerReplaySubject: ReplaySubject<PlayerModel>;
  private readonly reloadActivePlayerSubject: Subject<void>;

  private activePlayer: Observable<PlayerModel>;

  /**
   * Constructor
   *
   * @param http HttpClient
   */
  public constructor(private http: HttpClient) {
    this.changePlayerTheme = new BehaviorSubject<string>(null);
    this.changePlayer = new BehaviorSubject<PlayerModel>(null);

    this.activePlayerReplaySubject = new ReplaySubject<PlayerModel>(1);
    this.reloadActivePlayerSubject = new Subject<void>();
  }

  /**
   * Get all the players of the authenticated user
   *
   * @return Observable<PLayerModel[]>
   */
  public getPlayersForSelectScreen(): Observable<PlayerModel[]> {
    return this.http.get(API_ENDPOINT + 'get/player-select-list').pipe(
      map((response: { error: string, items: { players: PlayerModel[] } }) => {
        return response.items.players;
      }),
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  /**
   * Let backend know the player wants to switch their active player
   *
   * @param {PlayerModel} player
   *
   * @returns {Observable<boolean>}
   */
  public selectActivePlayer(player: PlayerModel): Observable<PlayerModel> {
    return this.http.post(API_ENDPOINT + 'set/active-player', {playerHash: player.iHash}).pipe(
      map((response: { error: string, items: PlayerModel }) => {
        return response.items;
      }),
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  /**
   * Trigger Player switch (mainly for player select)
   *
   * @return void
   */
  public updateCurrentPlayer(player: PlayerModel): void {
    this.changePlayerTheme.next(player.game.theme);
    this.changePlayer.next(player);
  }

  public get activePlayerSubject(): Observable<PlayerModel> {
    return this.activePlayerReplaySubject.asObservable();
  }

  /**
   * Gets the active player.
   *
   * @returns {Observable<PlayerModel>}
   */
  public getActivePlayer(): Observable<PlayerModel> {
    const refresh = this.forceRefresh.getValue();
    if (!this.activePlayer || refresh) {
      this.activePlayer = this.loadActivePlayer().pipe(
        takeUntil(this.reloadActivePlayerSubject),
        shareReplay(1),
      );

      if (refresh) {
        this.forceRefresh.next(false);
      }
    }

    return this.activePlayer;
  }

  /**
   * Loads the active player
   *
   * @return Observable<any>
   */
  public loadActivePlayer() {
    return this.http.get(API_ENDPOINT + 'get/active')
      .pipe(
        retry(3),
        map(
          (response: { error: string, items: PlayerModel }) => {
            return plainToClass(PlayerModel, response.items);
          },
        ),
        tap((player: PlayerModel) => {
          this.activePlayerReplaySubject.next(player);
        }),

      );
  }

  /**
   * Removes the mimic property from the active Player
   *
   * @return void
   */
  public removeMimicFromActivePlayer(): Observable<boolean> {
    return this.http.post(API_ENDPOINT + 'set/mimic/remove', {})
      .pipe(
        retry(3),
        map(
          (response: { error: string, items: { success: boolean } }) => {
            return response.items.success;
          },
        ),
      );
  }
}
